import type { Company, User } from '@commander/utils';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AUTH_STORAGE_KEYS } from '../constants';
import { useSharedAuth } from '../hooks/useSharedAuth';
import { clearAuthStorage, getCookie, setCookie } from '../lib/cookies';

export interface AuthState {
  user: User | null;
  selectedCompany: Company | null;
  isAuthenticated: boolean;
  token: string | null;
  permissions: string[];
  isLoading: boolean;
}

export interface AuthContextValue extends AuthState {
  login: (response: any) => Promise<void>;
  logout: () => void;
  selectCompany: (company: Company) => void;
  updateUser: (userData: Partial<User>) => void;
  refreshToken: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
  apiBaseUrl?: string;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    selectedCompany: null,
    isAuthenticated: false,
    token: null,
    permissions: [],
    isLoading: true,
  });

  // Initialize auth state from cookies (source of truth) and localStorage
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-time auth bootstrap on mount; refreshToken/logout are only called from the async init, re-running it on their identity change would re-initialize auth
  useEffect(() => {
    const initAuth = async () => {
      try {
        // IMPORTANT: Cookies are the source of truth for cross-port auth
        // Check cookies first (shared across all ports)
        const cookieToken = getCookie(AUTH_STORAGE_KEYS.TOKEN);
        const cookieUser = getCookie(AUTH_STORAGE_KEYS.USER);

        // Also check localStorage for this port
        const localToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
        const localUser = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
        const storedCompany = localStorage.getItem(AUTH_STORAGE_KEYS.SELECTED_COMPANY);

        // If cookies are empty but localStorage has data, it means user logged out from another port
        // Clear this port's localStorage to stay in sync
        if (!cookieToken && localToken) {
          await clearAuthStorage(AUTH_STORAGE_KEYS);
          setState((prev) => ({
            ...prev,
            isLoading: false,
          }));
          return;
        }

        // Use cookies as source of truth, fall back to localStorage if needed
        const storedToken = cookieToken || localToken;
        const storedUser = cookieUser || localUser;

        if (storedToken && storedUser) {
          const user = JSON.parse(storedUser);
          const company = storedCompany ? JSON.parse(storedCompany) : null;

          // Sync localStorage with cookies if they were out of sync
          if (cookieToken && cookieUser && !localToken) {
            localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, cookieToken);
            localStorage.setItem(AUTH_STORAGE_KEYS.USER, cookieUser);
          }

          setState((prev) => ({
            ...prev,
            user,
            selectedCompany: company,
            token: storedToken,
            isAuthenticated: true,
            permissions: user.permissions || [],
            isLoading: false,
          }));

          // Verify token is still valid
          await refreshToken();
        } else {
          setState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      } catch (error) {
        console.error('[AuthProvider] Failed to initialize auth:', error);
        logout();
      }
    };

    initAuth();
  }, []);

  // Listen for storage changes (logout from micro-apps or other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // If auth token was removed (logout from micro-app or another tab)
      if (e.key === AUTH_STORAGE_KEYS.TOKEN && e.newValue === null) {
        setState({
          user: null,
          selectedCompany: null,
          isAuthenticated: false,
          token: null,
          permissions: [],
          isLoading: false,
        });
      }
      // If auth token was added (login from another tab)
      else if (e.key === AUTH_STORAGE_KEYS.TOKEN && e.newValue) {
        const storedUser = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
        const storedCompany = localStorage.getItem(AUTH_STORAGE_KEYS.SELECTED_COMPANY);

        if (storedUser) {
          const user = JSON.parse(storedUser);
          const company = storedCompany ? JSON.parse(storedCompany) : null;

          setState({
            user,
            selectedCompany: company,
            token: e.newValue,
            isAuthenticated: true,
            permissions: user.permissions || [],
            isLoading: false,
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = useCallback(async (response: any) => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      if (response?.status === 200) {
        const { token, user } = response.data.data;

        // Map user data to AuthState format
        const userPermissions = user.user_products?.map((p: any) => p.product.name) || [];

        // Handle name with fallback for missing first/last name
        const userName =
          user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name}`.trim()
            : user.email?.split('@')[0] || 'User';

        const userAvatar = user.photo_url || undefined;
        // Create mapped user object matching the User type from @commander/utils
        const mappedUser = {
          id: user.id,
          email: user.email,
          name: userName,
          avatar: userAvatar,
          data_generated: user.data_generated || false,
          company_employee: user.company_employee || null,
          role: user.role || 'user',
          permissions: userPermissions,
          onboarding_complete: user.onboarding_complete || false,
        };
        // Store mapped user in both localStorage AND cookies (cookies work across ports)
        localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
        localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(mappedUser));
        localStorage.setItem(AUTH_STORAGE_KEYS.USER_DETAILS, JSON.stringify(user)); // Keep raw user for reference

        // Also store in cookies for cross-port access (localhost:3000, localhost:3001, etc.)
        setCookie(AUTH_STORAGE_KEYS.TOKEN, token);
        setCookie(AUTH_STORAGE_KEYS.USER, JSON.stringify(mappedUser));

        setState((prev) => ({
          ...prev,
          user: mappedUser,
          token,
          permissions: userPermissions,
          isAuthenticated: true,
          isLoading: false,
        }));
      } else {
        throw new Error(response?.response?.data?.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('[AuthProvider] Login error:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    setState({
      user: null,
      selectedCompany: null,
      isAuthenticated: false,
      token: null,
      permissions: [],
      isLoading: false,
    });

    // Clear all auth storage and wait for completion
    await clearAuthStorage(AUTH_STORAGE_KEYS);
    await window.location.reload();

    // Now redirect to Home login page - storage is guaranteed to be cleared
    const shellUrl = import.meta.env.VITE_SHELL_URL;
    const loginUrl =
      shellUrl.startsWith('http://') || shellUrl.startsWith('https://')
        ? `${shellUrl}/login`
        : `http://${shellUrl}/login`;
    window.location.href = loginUrl;

    // Don't redirect here - let React Router handle it via ProtectedRoutes
    // Storage is now guaranteed to be cleared before React Router navigates
  }, []);

  const selectCompany = useCallback((company: Company) => {
    localStorage.setItem(AUTH_STORAGE_KEYS.SELECTED_COMPANY, JSON.stringify(company));
    setState((prev) => ({
      ...prev,
      selectedCompany: company,
    }));
  }, []);

  const updateUser = useCallback((userData: Partial<User>) => {
    setState((prev) => {
      if (!prev.user) {
        console.warn('[AuthProvider] updateUser called but no user is currently authenticated');
        return prev;
      }

      const updatedUser = { ...prev.user, ...userData };

      // Update localStorage
      localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(updatedUser));

      // Update cookies for cross-port sharing
      setCookie(AUTH_STORAGE_KEYS.USER, JSON.stringify(updatedUser));

      // Update state
      return {
        ...prev,
        user: updatedUser,
      };
    });
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      // Check cookies first (cross-port auth), then localStorage
      const token =
        getCookie(AUTH_STORAGE_KEYS.TOKEN) || localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
      if (!token) {
        logout();
        return;
      }

      // TODO: Implement real token refresh with API call
      // For now, token validation happens in the interceptor
      // This is a placeholder for future token refresh implementation
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  }, [logout]);

  const hasPermission = useCallback(
    (permission: string) => {
      return state.permissions.includes(permission);
    },
    [state.permissions]
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]) => {
      return permissions.some((permission) => state.permissions.includes(permission));
    },
    [state.permissions]
  );

  const contextValue: AuthContextValue = {
    ...state,
    login,
    logout,
    selectCompany,
    updateUser,
    refreshToken,
    hasPermission,
    hasAnyPermission,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  const sharedAuth = useSharedAuth();

  // If no AuthProvider exists (micro-frontends), use shared auth from localStorage
  if (context === undefined) {
    // Return a compatible interface
    return {
      ...sharedAuth,
      selectedCompany: null,
      token: localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN),
      permissions: sharedAuth.user?.permissions || [],
      login: async () => {
        throw new Error('Login must be done through Home app');
      },
      selectCompany: () => {
        throw new Error('Company selection must be done through Home app');
      },
      updateUser: (userData: Partial<User>) => {
        if (sharedAuth.user) {
          const updatedUser = { ...sharedAuth.user, ...userData };
          localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(updatedUser));
          setCookie(AUTH_STORAGE_KEYS.USER, JSON.stringify(updatedUser));
        }
      },
      refreshToken: async () => {
        // No-op for micro-frontends
        return Promise.resolve();
      },
      hasPermission: (permission: string) => {
        return sharedAuth.user?.permissions?.includes(permission) || false;
      },
      hasAnyPermission: (permissions: string[]) => {
        return permissions.some((p) => sharedAuth.user?.permissions?.includes(p)) || false;
      },
    };
  }

  return context;
}
